import * as jud from "@/services/jud.service";
import { session, decodeJwt } from "@/services/session";

/**
 * Who the user is and how long they have.
 *
 * The durable copy of all of this lives in localStorage (`services/session.js`) so it
 * survives a reload; this module mirrors it into reactive state for the UI and owns the
 * one async transition — exchanging the inbound token for a session.
 */
export default {
  namespaced: true,

  state: () => ({
    trackingId: "",
    /** JWT `exp`, epoch seconds. 0 means "no session". */
    expiresAt: 0,
    authenticated: false,
    returnUrl: "",
  }),

  getters: {
    isAuthenticated: (state) => state.authenticated,
    /** Seconds left, floored at 0. The ticking itself belongs to the countdown component. */
    secondsLeft: (state) => {
      if (!state.expiresAt) return 0;
      return Math.max(0, Math.floor(state.expiresAt - Date.now() / 1000));
    },
  },

  mutations: {
    SET_SESSION(state, { trackingId, expiresAt, returnUrl }) {
      state.trackingId = trackingId || "";
      state.expiresAt = expiresAt || 0;
      state.returnUrl = returnUrl || "";
      state.authenticated = true;
    },
    /** Hydrate from localStorage on boot, without a round trip. */
    RESTORE(state, { trackingId, expiresAt, returnUrl, authenticated }) {
      state.trackingId = trackingId || "";
      state.expiresAt = expiresAt || 0;
      state.returnUrl = returnUrl || "";
      state.authenticated = !!authenticated;
    },
    CLEAR(state) {
      state.trackingId = "";
      state.expiresAt = 0;
      state.returnUrl = "";
      state.authenticated = false;
    },
  },

  actions: {
    /** Read whatever a previous page load left behind. */
    restore({ commit }) {
      commit("RESTORE", {
        trackingId: session.trackingId(),
        expiresAt: session.expiresAt(),
        returnUrl: session.returnUrl(),
        authenticated: session.isAuthenticated(),
      });
    },

    /**
     * Exchange the inbound URL token for a session.
     * Rejects with the axios error so the view can surface the backend's message.
     */
    async checkToken({ commit }, { token, returnUrl }) {
      const { id, accessToken } = await jud.checkToken(token);
      if (!accessToken) {
        throw new Error("دریافت اطلاعات با خطا مواجه شد. لطفاً دوباره تلاش کنید.");
      }
      const claims = decodeJwt(accessToken);
      const exp = (claims && claims.exp) || 0;

      session.start({ accessToken, id, exp, token, returnUrl });
      commit("SET_SESSION", { trackingId: id, expiresAt: exp, returnUrl });
      return { id, accessToken };
    },

    /** The session is spent but the tracking id and return URL are still needed. */
    finish({ state, commit }) {
      session.endAuth();
      commit("RESTORE", {
        trackingId: state.trackingId,
        expiresAt: 0,
        returnUrl: state.returnUrl,
        authenticated: false,
      });
    },

    /** Full wipe — expiry, an explicit restart, or a 401. */
    clear({ commit }) {
      session.clear();
      commit("CLEAR");
    },
  },
};
